/*
 *
 * Copyright (C) 2022 NetBase Solutions, Inc.
 * 3960 Freedom Circle Suite 201 Santa Clara, CA 95054
 * All rights reserved.
 */

import React from 'react'
import styled from 'styled-components'

import { SocialWebSourcesWidget } from 'schemas/SocialWebSourcesWidgetSchema'
import { InsightCount } from 'schemas/action/InsightCountSchema'
import { Widget } from 'features/types'
import messageUtil from 'utils/messageUtil'

import { useDashboardWidgetSetting } from '../../../../../hooks/useDashboardState'
import { useDashboardWidgetData } from '../../../../../hooks/useWidgetState'
import { usePageConfigUser } from '../../../../../hooks/usePageConfig'
import DonutChart from '../../../../visualization/Donut'
import { colorSettings } from '../../utils'
import { getProvisionedInsightDataSet } from '../utils'

/*
 * NI-2503 — Source chart outer-label overflow.
 *
 * Goal (per customer): keep the donut at its ORIGINAL full panel size
 * AND don't let the south-pole outer label of a dominant slice (e.g.
 * Twitter 95 %+) appear cut off.
 *
 * Earlier iterations (PRs #91/#93/#95) tried to make room for the
 * label by stealing pixels from the donut — bottom padding 32 → 56 →
 * 72 px on the chart wrapper. Every step shrank the donut visibly
 * and the customer rejected each one. Padding cannot satisfy both
 * goals at the same time inside a fixed-height panel cell.
 *
 * The right structural answer is to stop fighting the chart's
 * geometry and instead let the label render PAST the panel border
 * into the dashboard row gap. `WidgetContainer/index.tsx` flips
 * `SOCIAL_WEB_SOURCES` (and `SOCIAL_WEB_SENTIMENT` /
 * `AUDIENCE_SENTIMENT`) to `overflow: visible` on both the outer
 * `StyledContainer` and inner `StyledContent`. But that's still not
 * enough on its own — Highcharts renders the donut into an `<svg>`
 * element that the browser defaults to a clipping viewport: any
 * `<text>` (the data labels) drawn at SVG coordinates past the
 * element's width/height gets clipped at the SVG box even when
 * every HTML ancestor is `overflow: visible`. The fix is to force
 * `overflow: visible` on the SVG itself via a descendant selector,
 * so the south-pole label can paint into the dashboard row gap.
 *
 * The customer explicitly OK'd the "label overlaps the black border"
 * outcome ("let the text overwrite the black boundary or let black
 * boundary becomes thiness") in preference to a shrunken donut.
 */
export const SourceDonutContainer = styled.div`
  width: 100%;
  height: 100%;

  /* Let Highcharts data labels paint past the SVG's nominal box.
     The Highcharts chart wraps its drawing in
       div.highcharts-container > svg.highcharts-root
     and both default to clipping content at their box. The chart
     wrappers are sized to the donut's plot area, so a south-pole
     outer data label (which Highcharts positions OUTSIDE the slice)
     can sit a few pixels past the SVG's bottom edge — overflow on
     these inner elements clips it even when every HTML ancestor is
     already overflow:visible.

     Three layers can clip the label, all defeated here:
       1. The HTML div wrapper (\`.highcharts-container\`) — \`overflow\`
       2. The root SVG (\`.highcharts-root\` / \`svg\`) — \`overflow\`
       3. Highcharts' own \`<clipPath>\` it applies to the plot/series
          group via \`clip-path\` on \`<g>\` nodes — \`clip-path: none\`
          on the data-label groups specifically.
     The wildcard \`& *\` belt-and-suspenders catches any descendant
     we missed (e.g. future Highcharts internal name changes). */
  & * {
    overflow: visible !important;
  }

  & .highcharts-data-labels,
  & .highcharts-data-label,
  & .highcharts-data-label-connector {
    clip-path: none !important;
    clip: auto !important;
  }
`

const Donut: Widget = ({ widgetId }) => {
  const widgetSetting = useDashboardWidgetSetting<SocialWebSourcesWidget>(widgetId)
  const widgetData = useDashboardWidgetData<InsightCount>(widgetId)
  const pageConfigUser = usePageConfigUser()

  const filteredData = getProvisionedInsightDataSet(widgetData, pageConfigUser)
  const donutData = filteredData.map((dataEntry) => ({
    y: dataEntry.value,
    name: messageUtil.sources(dataEntry.name!)
  }))

  return (
    <SourceDonutContainer>
      <DonutChart
        data={donutData}
        colorSettings={colorSettings}
        seriesName={messageUtil.countType(widgetSetting.countType!)}
      />
    </SourceDonutContainer>
  )
}

export default Donut
